import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuthStore } from "./store/authStore";

import LoginPage from "./pages/LoginPage";
import LiveViewPage from "./pages/LiveViewPage";
import PlaybackPage from "./pages/PlaybackPage";
import DevicesPage from "./pages/DevicesPage";
import DeviceConfigPage from "./pages/DeviceConfigPage";
import DownloadsPage from "./pages/DownloadsPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AppShell from "./components/Layout/AppShell";
import { FlagProtectedRoute } from "./components/common/FlagProtectedRoute";

function ProtectedRoute({
  children,
  allowPasswordChange = false,
}: {
  children: React.ReactNode;
  allowPasswordChange?: boolean;
}) {
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user?.must_change_password && !allowPasswordChange) {
    return <Navigate to="/change-password" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <div className="p-4 text-muted text-sm">Cargando...</div>;
  if (user.role !== "admin") return <Navigate to="/live" replace />;
  return <>{children}</>;
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute allowPasswordChange>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/live" replace />} />
        <Route
          path="/live"
          element={
            <FlagProtectedRoute permission="can_view_live">
              <LiveViewPage />
            </FlagProtectedRoute>
          }
        />
        <Route
          path="/playback"
          element={
            <FlagProtectedRoute permission="can_view_recordings">
              <PlaybackPage />
            </FlagProtectedRoute>
          }
        />
        <Route
          path="/devices"
          element={
            <FlagProtectedRoute permission="can_manage_devices">
              <DevicesPage />
            </FlagProtectedRoute>
          }
        />
        <Route
          path="/devices/:id/config"
          element={
            <FlagProtectedRoute permission="can_access_config">
              <DeviceConfigPage />
            </FlagProtectedRoute>
          }
        />
        <Route
          path="/downloads"
          element={
            <FlagProtectedRoute permission="can_download_recordings">
              <DownloadsPage />
            </FlagProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <FlagProtectedRoute permission="can_manage_users">
              <UsersPage />
            </FlagProtectedRoute>
          }
        />
        <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/live" replace />} />
    </Routes>
  );
}
